import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

const TARGET_WIDTH = 900;
const TARGET_HEIGHT = 1200;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const PortraitCropModal = ({ isOpen, onClose, onCropComplete, initialFile, preLoadedImageSrc }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Load image with retry logic for iOS/Android iCloud photos (fallback only)
  const loadImageWithRetry = React.useCallback(async (file, attempt = 0) => {
    const MAX_RETRIES = 3;
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        setImageSrc(reader.result);
        setLoading(false);
        setLoadError(null);
        resolve(true);
      };
      
      reader.onerror = async (error) => {
        console.error(`FileReader error (attempt ${attempt + 1}):`, error);
        
        if (attempt < MAX_RETRIES) {
          // Wait progressively longer between retries
          const delay = 500 * (attempt + 1);
          console.log(`Retrying in ${delay}ms...`);
          await new Promise(r => setTimeout(r, delay));
          
          try {
            await loadImageWithRetry(file, attempt + 1);
            resolve(true);
          } catch (err) {
            reject(err);
          }
        } else {
          const errorMsg = 'Failed to load photo after multiple attempts. The file may still be downloading from iCloud or cloud storage. Please wait a moment and try again.';
          setLoadError(errorMsg);
          setLoading(false);
          reject(new Error(errorMsg));
        }
      };
      
      reader.readAsDataURL(file);
    });
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      // Reset state
      setImageSrc(null);
      setLoadError(null);
      
      // If we have a pre-loaded image, use it immediately (fast path)
      if (preLoadedImageSrc) {
        console.log('Using pre-loaded image data URL');
        setImageSrc(preLoadedImageSrc);
        setLoading(false);
        return;
      }
      
      // Otherwise fall back to reading the file (slow path - should rarely happen)
      if (initialFile) {
        console.log('No pre-loaded image, reading file...');
        
        if (initialFile.size > MAX_FILE_SIZE) {
          setLoadError(`File size exceeds 20MB limit`);
          return;
        }
        
        // Start loading with retry logic
        setLoading(true);
        loadImageWithRetry(initialFile, 0).catch(err => {
          console.error('Failed to load image:', err);
          // Error already set in loadImageWithRetry
        });
      }
    }
  }, [initialFile, preLoadedImageSrc, isOpen, loadImageWithRetry]);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const getCroppedImg = async () => {
    if (!imageSrc || !croppedAreaPixels) return null;

    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Set canvas to cropped area size
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    // Draw the cropped image
    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );

    // Resize to target dimensions
    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = TARGET_WIDTH;
    resizedCanvas.height = TARGET_HEIGHT;
    const resizedCtx = resizedCanvas.getContext('2d');

    try {
      // Try using pica for high-quality resize
      const pica = require('pica')();
      await pica.resize(canvas, resizedCanvas, {
        quality: 3,
        unsharpAmount: 80,
        unsharpRadius: 0.6,
        unsharpThreshold: 2
      });
    } catch (picaError) {
      console.warn('Pica resize failed (fingerprinting protection?), using browser resize:', picaError);
      // Fallback: Use browser's native canvas resize (lower quality but works)
      resizedCtx.drawImage(canvas, 0, 0, TARGET_WIDTH, TARGET_HEIGHT);
    }

    // Convert to JPEG blob
    return new Promise((resolve) => {
      resizedCanvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/jpeg',
        0.85
      );
    });
  };

  const handleCropSubmit = async () => {
    setProcessing(true);
    try {
      const croppedBlob = await getCroppedImg();
      if (croppedBlob) {
        const file = new File([croppedBlob], initialFile.name, { type: 'image/jpeg' });
        await onCropComplete(file);
        onClose();
      }
    } catch (error) {
      console.error('Error cropping image:', error);
      alert('Failed to process image');
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl md:max-h-[90vh] h-full md:h-auto w-full md:w-auto max-h-screen overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg md:text-xl">Crop Image (Portrait 3:4)</DialogTitle>
        </DialogHeader>

        {loading && !imageSrc && !loadError && (
          <div className="flex flex-col items-center justify-center py-16 space-y-4">
            <Loader2 className="w-16 h-16 animate-spin text-blue-500" />
            <div className="text-center space-y-2">
              <p className="text-lg font-medium">Loading Photo...</p>
              <p className="text-sm text-gray-500">
                Please wait while your photo loads.
              </p>
              <p className="text-xs text-gray-400">
                This may take a moment for iCloud or large files.
              </p>
            </div>
          </div>
        )}

        {loadError && (
          <div className="flex flex-col items-center justify-center py-16 space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg max-w-md">
              <p className="text-sm text-red-600 text-center mb-4">{loadError}</p>
              <Button
                onClick={() => {
                  if (initialFile) {
                    setLoadError(null);
                    setLoading(true);
                    loadImageWithRetry(initialFile, 0).catch(console.error);
                  }
                }}
                className="w-full min-h-[48px]"
                variant="outline"
              >
                Try Again
              </Button>
            </div>
            <Button
              onClick={handleClose}
              className="min-h-[48px]"
              variant="outline"
            >
              Cancel
            </Button>
          </div>
        )}

        {imageSrc && !loadError && (
          <div className="space-y-4 md:space-y-6">
            <div className="relative w-full h-[400px] md:h-[500px] bg-gray-900">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={3 / 4}
                onCropChange={onCropChange}
                onZoomChange={onZoomChange}
                onCropComplete={onCropCompleteCallback}
                restrictPosition={true}
                showGrid={false}
              />
            </div>

            <div className="space-y-2 px-2">
              <Label className="text-base">Zoom</Label>
              <Slider
                value={[zoom]}
                onValueChange={(values) => setZoom(values[0])}
                min={1}
                max={3}
                step={0.01}
                className="w-full"
              />
            </div>

            <div className="flex gap-3 pt-4 px-2">
              <Button
                onClick={handleCropSubmit}
                disabled={processing}
                className="flex-1 bg-blue-500 hover:bg-blue-600 min-h-[48px] md:min-h-[44px] text-base"
                type="button"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Crop & Upload'
                )}
              </Button>
              <Button 
                onClick={handleClose} 
                variant="outline" 
                disabled={processing}
                className="min-h-[48px] md:min-h-[44px] text-base"
                type="button"
              >
                Cancel
              </Button>
            </div>

            <p className="text-xs md:text-sm text-gray-500 text-center px-2">
              Image will be resized to {TARGET_WIDTH}×{TARGET_HEIGHT}px portrait format
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Helper function to create image from data URL
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

export default PortraitCropModal;
